import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  useAlert,
} from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSerie } from '@/features/series-management';
import {
  DeleteSerieVideoModal,
  SERIE_VIDEO_PAGE_SIZE,
  SerieVideoSearchBar,
  SerieVideoTable,
  useDeleteSerieVideo,
  useSerieVideoFilters,
  useSerieVideoList,
} from '@/features/series-video-management';
import type { SerieVideoGridItemViewModel } from '@/shared/api/generated';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export const Route = createFileRoute('/_authenticated/series/$serieId/videos/')({
  component: SerieVideoListPage,
});

/** Página de listagem de Vídeos de uma Série. */
export function SerieVideoListPage() {
  const { t } = useTranslation('series');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const { serieId: serieIdParam } = Route.useParams();
  const serieId = Number(serieIdParam);

  const { serie } = useSerie(serieId);
  const { filters, updateFilters } = useSerieVideoFilters();
  const { videos, totalCount, totalPages, currentPage, isLoading, isError, refetch } =
    useSerieVideoList(serieId, filters);
  const deleteSerieVideo = useDeleteSerieVideo(serieId);

  const [deleting, setDeleting] = useState<SerieVideoGridItemViewModel | null>(null);

  const handleDeleteConfirm = () => {
    if (deleting?.id == null) return;
    deleteSerieVideo.mutate(
      { id: deleting.id, params: { serieId } },
      {
        onSuccess: () => alert({ alertType: 'success', title: t('videos.toast.deleteSuccess') }),
        onError: () => alert({ alertType: 'warning', title: t('videos.toast.deleteError') }),
        onSettled: () => setDeleting(null),
      },
    );
  };

  const addButton = (
    <Button
      hierarchy="primary"
      size="lg"
      leadingIcon={PlusIcon}
      onClick={() =>
        void navigate({ to: '/series/$serieId/videos/create', params: { serieId: serieIdParam } })
      }
    >
      {t('videos.actions.add')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-mini">
      <PageHeader
        title={t('videos.title')}
        subtitle={
          serie?.title
            ? t('videos.subtitle', { serie: serie.title, count: totalCount })
            : t('videos.subtitleNoSerie', { count: totalCount })
        }
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to="/series">{t('videos.breadcrumb.series')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('videos.breadcrumb.videos')}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={addButton}
      />

      {isError ? (
        <div className="flex flex-col items-start gap-nano rounded-md border-hairline border-base-danger-300 bg-ctx-layout-surface p-xxs">
          <p className="text-base-danger-700 ts-paragraph-xs">{t('videos.toast.loadError')}</p>
          <Button hierarchy="secondary" size="md" onClick={() => void refetch()}>
            {t('videos.loadErrorRetry')}
          </Button>
        </div>
      ) : (
        <>
          <SerieVideoSearchBar
            defaultValue={filters.search}
            onSearch={search => updateFilters({ search, page: 1 })}
            onClear={() => updateFilters({ search: '', page: 1 })}
          />

          <SerieVideoTable
            data={videos}
            isLoading={isLoading}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={SERIE_VIDEO_PAGE_SIZE}
            onPageChange={page => updateFilters({ page })}
            emptyStateAction={addButton}
            onEdit={video =>
              void navigate({
                to: '/series/$serieId/videos/$videoId',
                params: { serieId: serieIdParam, videoId: String(video.id) },
              })
            }
            onManageLangs={video =>
              void navigate({
                to: '/series/$serieId/videos/$videoId/langs',
                params: { serieId: serieIdParam, videoId: String(video.id) },
              })
            }
            onDelete={setDeleting}
          />
        </>
      )}

      <DeleteSerieVideoModal
        open={deleting !== null}
        videoTitle={deleting?.langName ?? ''}
        onConfirm={handleDeleteConfirm}
        onOpenChange={open => {
          if (!open) setDeleting(null);
        }}
      />
    </div>
  );
}
